/**
 * Header strip above the write-back textarea: BO status dropdown,
 * EV write-back chip (with error tooltip when status is `Error`), and the
 * "view trail" eye icon (visible when EV write-back is `Completed`).
 *
 * Pure presentational — receives all data and callbacks via props.
 *
 * @module busniessOffice/components/singleComment/WriteBackHeader
 */

import { TagOption, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import ToolTipUsingPortal from "../../../../../../components/toolTipUsingPortal/toolTipUsingPortal";
import { BadgeWithIconProps } from "../../../../../../data-model/badgeWithIconProps";
import EyeIcon from "../../../../../../svg/eye";
import { isValidString } from "../../../../../../utils/stringModifications";
import { EvWriteBackStatusLabel } from "./types";

/**
 * Props for the write-back header strip in the BO comment panel.
 */
interface WriteBackHeaderProps {
  boStatus: string;
  boStatusOptions: BadgeWithIconProps[];
  boOptionsLoading: boolean;
  evStatusLabel: EvWriteBackStatusLabel;
  evStatusColor: string;
  onBoStatusSelect: (badge: string) => void;
  onBoStatusDefaultClick: () => void;
  onEvCompletedEyeClick: () => void;
  /** EV write-back failure detail shown in the Error-status hover tooltip. */
  errorMessage?: string;
}

/**
 * Maps BO status badges into Oasis tag options used by the dropdown tag.
 *
 * @param boStatusOptions - Available BO status options from the API
 * @returns Oasis-compatible tag option list
 */
const getBoStatusTagOptions = (
  boStatusOptions: BadgeWithIconProps[],
): TagOption[] => {
  return boStatusOptions.map((option) => ({
    label: option.text,
    value: option.id,
    backgroundColor: option.bgColor,
    textColor: option.textColor,
  }));
};

/**
 * Renders the write-back header with BO status picker and EV status.
 *
 * When `evStatusLabel` is `Error`, hovering the status shows `errorMessage`
 * in a portaled tooltip.
 *
 * @param props - Header presentation props and click handlers
 * @returns Header UI for BO/EV write-back status section
 */
const WriteBackHeader = ({
  boStatus,
  boStatusOptions,
  boOptionsLoading,
  evStatusLabel,
  evStatusColor,
  onBoStatusSelect,
  onBoStatusDefaultClick,
  onEvCompletedEyeClick,
  errorMessage,
}: WriteBackHeaderProps) => {
  const commentModalRef = useRef<HTMLDivElement>(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  return (
    <div className="write-back-bo-status flex items-center gap-2 border-b border-primaryGray-14 p-3">
      <div className="write-back-bo-status--radio text-h12 font-normal">
        Radio Button :
      </div>
      <div className="write-back-bo-status--badge-dropdown">
        {boOptionsLoading ? (
          <div className="loading-status--container flex items-center gap-2 text-sm">
            <SpinningLoader />
            <div className="italic text-primaryGray-5">
              Loading Status List...
            </div>
          </div>
        ) : (
          <div onClick={onBoStatusDefaultClick}>
            <Tags
              label={boStatus}
              backgroundColor="#F5F5F5"
              textColor="#0F0F0F"
              options={getBoStatusTagOptions(boStatusOptions)}
              onSelect={(option) => {
                onBoStatusSelect(option.value);
              }}
              shouldUsePortal={true}
            />
          </div>
        )}
      </div>
      <div
        className={`ev-write-back status ml-auto text-tiny font-semibold italic ${evStatusColor}`}
        ref={commentModalRef}
        onMouseEnter={() => setIsCommentModalOpen(true)}
        onMouseLeave={() => setIsCommentModalOpen(false)}
      >
        {evStatusLabel}
        {evStatusLabel === "Error" && isValidString(errorMessage) && (
          <ToolTipUsingPortal
            children={errorMessage}
            placement={"left"}
            showTooltip={isCommentModalOpen}
            parentRef={commentModalRef}
            wrapText={true}
          />
        )}
      </div>
      {evStatusLabel === "Completed" && (
        <EyeIcon
          color="#008F32"
          className="cursor-pointer"
          onClick={onEvCompletedEyeClick}
        />
      )}
    </div>
  );
};

export default WriteBackHeader;
